<script lang="ts">
  // Timezone-aware effective-from picker per
  // INTERFACE-SPECIFICATION.md §7.3.
  //
  // Reads the jurisdiction timezone from the catalogue file. Displays
  // the resolved UTC instant under the picker. Surfaces DST gap and
  // overlap states so the user resolves them client-side before the
  // proxy 422s.

  import { resolveLocalToUtc, type TimezoneResolution } from '../lib/timezone';

  interface Props {
    timezone: string;
    localIso: string;
    onLocalIso: (value: string) => void;
    // onAmbiguousChoice fires when the user picks one of the two
    // occurrences on a DST fall-back date. occurrence is 'first' on
    // the earlier (still summer time) instant and 'second' on the
    // later (standard time) instant. The chosen occurrence travels
    // with the submission payload as effective_from_occurrence so
    // the server resolves the same instant the user saw — see
    // SPEC-CHANGE-CANDIDATES item 11 and tz-utils.localToUtc.
    onAmbiguousChoice: (utc: string, abbreviation: string, occurrence: 'first' | 'second') => void;
  }

  const { timezone, localIso, onLocalIso, onAmbiguousChoice }: Props = $props();

  const datePart = $derived(localIso.split('T')[0] ?? '');
  const timePart = $derived(localIso.split('T')[1]?.slice(0, 5) ?? '00:00');

  function setDate(event: Event) {
    const target = event.target as HTMLInputElement;
    onLocalIso(target.value + 'T' + timePart + ':00');
  }
  function setTime(event: Event) {
    const target = event.target as HTMLInputElement;
    onLocalIso(datePart + 'T' + target.value + ':00');
  }

  const resolved = $derived<TimezoneResolution>(localIso ? resolveLocalToUtc(localIso, timezone) : { status: 'non_existent' });
</script>

<div class="picker">
  <div class="picker__row">
    <label>
      <span class="muted">Date</span>
      <input type="date" value={datePart} onchange={setDate} />
    </label>
    <label>
      <span class="muted">Time</span>
      <input type="time" value={timePart} onchange={setTime} />
    </label>
    <span class="muted picker__timezone">{timezone}</span>
  </div>

  {#if resolved.status === 'ok'}
    <p class="muted picker__resolved">
      Stored as <code>{resolved.utc}</code> (UTC) · {resolved.tz_abbreviation}
    </p>
  {:else if resolved.status === 'non_existent'}
    <div class="banner banner-fail">
      This time does not exist in {timezone} (daylight saving start). Choose either 01:59 or 03:00 on the affected date.
    </div>
  {:else if resolved.status === 'ambiguous'}
    <div class="banner banner-warn">
      <strong>This time occurs twice on the chosen date.</strong>
      <div class="picker__choices">
        <button type="button" class="btn" onclick={() => onAmbiguousChoice(resolved.first_utc ?? '', resolved.first_abbr ?? '', 'first')}>
          First occurrence — {resolved.first_abbr} <span class="muted">({resolved.first_utc})</span>
        </button>
        <button type="button" class="btn" onclick={() => onAmbiguousChoice(resolved.second_utc ?? '', resolved.second_abbr ?? '', 'second')}>
          Second occurrence — {resolved.second_abbr} <span class="muted">({resolved.second_utc})</span>
        </button>
      </div>
    </div>
  {/if}
</div>

<style>
  .picker { display: flex; flex-direction: column; gap: var(--space-2); }
  .picker__row { display: flex; gap: var(--space-3); align-items: end; flex-wrap: wrap; }
  .picker__timezone { font-family: var(--font-mono); padding-bottom: var(--space-1); }
  .picker__resolved code { font-family: var(--font-mono); }
  .picker__choices { display: flex; gap: var(--space-3); margin-top: var(--space-2); flex-wrap: wrap; }
</style>
